import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useRef, useState, } from 'react';
const ViewportActionsContext = createContext(null);
ViewportActionsContext.displayName = 'ViewportActionsContext';
/**
 * A handler slot in a ref, so a registration re-renders no consumer. The
 * cleanup clears the slot only while it holds that handler, since a newer
 * canvas may replace it before the old one unmounts. A consumer that must know
 * when the slot fills subscribes, as `useSyncExternalStore` does.
 */
function useHandlerSlot() {
    const ref = useRef(null);
    const listeners = useRef(new Set());
    const notify = useCallback(() => {
        for (const listener of listeners.current)
            listener();
    }, []);
    const register = useCallback((handler) => {
        ref.current = handler;
        notify();
        return () => {
            if (ref.current === handler) {
                ref.current = null;
                notify();
            }
        };
    }, [notify]);
    const subscribe = useCallback((listener) => {
        listeners.current.add(listener);
        return () => {
            listeners.current.delete(listener);
        };
    }, []);
    return { ref, register, subscribe };
}
export function ViewportActionsProvider({ children, initialPreviewedCameraPath = null, }) {
    const [previewedCameraPath, setPreviewedCameraPath] = useState(initialPreviewedCameraPath);
    const [frame2D, setFrame2D] = useState(null);
    const frame2DIdRef = useRef(0);
    /** Written only by `claimFrame2D`. Request ids start at 1, so 0 claims nothing. */
    const claimedFrame2DIdRef = useRef(0);
    const previewCamera = useCallback((path) => {
        setPreviewedCameraPath(path);
    }, []);
    const requestFrame2D = useCallback((view) => {
        frame2DIdRef.current += 1;
        setFrame2D({ ...view, requestId: frame2DIdRef.current });
    }, []);
    const claimFrame2D = useCallback((requestId) => {
        if (requestId <= claimedFrame2DIdRef.current)
            return false;
        claimedFrame2DIdRef.current = requestId;
        return true;
    }, []);
    const endCameraPreview = useCallback(() => {
        setPreviewedCameraPath(null);
    }, []);
    const { ref: resetHandlerRef, register: registerResetHandler } = useHandlerSlot();
    const resetCamera = useCallback(() => {
        endCameraPreview();
        resetHandlerRef.current?.();
    }, [endCameraPreview, resetHandlerRef]);
    const { ref: screenshotSourceRef, register: registerScreenshotSource, subscribe: subscribeScreenshotHandler, } = useHandlerSlot();
    const registerScreenshotHandler = useCallback((capture, renderedScene = null) => registerScreenshotSource({ capture, renderedScene }), [registerScreenshotSource]);
    const takeScreenshot = useCallback(() => {
        return screenshotSourceRef.current?.capture() ?? null;
    }, [screenshotSourceRef]);
    const hasScreenshotHandler = useCallback(() => screenshotSourceRef.current !== null, [screenshotSourceRef]);
    const screenshotScene = useCallback(() => screenshotSourceRef.current?.renderedScene ?? null, [screenshotSourceRef]);
    const value = useMemo(() => ({
        previewedCameraPath,
        previewCamera,
        endCameraPreview,
        frame2D,
        requestFrame2D,
        claimFrame2D,
        resetCamera,
        registerResetHandler,
        takeScreenshot,
        registerScreenshotHandler,
        hasScreenshotHandler,
        screenshotScene,
        subscribeScreenshotHandler,
    }), [
        previewedCameraPath,
        previewCamera,
        endCameraPreview,
        frame2D,
        requestFrame2D,
        claimFrame2D,
        resetCamera,
        registerResetHandler,
        takeScreenshot,
        registerScreenshotHandler,
        hasScreenshotHandler,
        screenshotScene,
        subscribeScreenshotHandler,
    ]);
    return _jsx(ViewportActionsContext.Provider, { value: value, children: children });
}
/** The viewport actions, or null when no provider is mounted. */
export function useOptionalViewportActions() {
    return useContext(ViewportActionsContext);
}
//# sourceMappingURL=ViewportActionsContext.js.map